import { v4 as uuidv4 } from 'uuid';
import appManager from '../../app/appManager';
import AppModule from '../../app/lib/AppModule';
import { diagnosticsState } from './lib/state';
import {
  TrackErrorInput,
  TrackEventInput,
  TrackedError,
  TrackedErrorPriority,
  TrackedErrorType,
  TrackedEvent,
  FrontendMetricEntry,
  MetricType,
} from './lib/types';
import * as Sentry from '@sentry/react';
import { sentryErrorIntegration } from './SentryErrorIntegration';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import environment from '@lib/environment';
import { CallDirection } from '@api/generated/graphql';
import DatadogModule from '@modules/DatadogModule';

// Re-export CallDirection for convenience
export { CallDirection };

const ERROR_IGNORE_LIST = [
  'window.require is not implemented', // workaround for vite preview (see index.html)
  'Requested device not found', // side effect of MFE soft phone
];

/**
 * Metric name constants for frontend metrics.
 * These names will be prefixed with "contactcenter_fe_" by the BFF.
 */
export const MetricNames = {
  ERROR_TOTAL: 'error_total',
  LATENCY_DURATION: 'latency_duration',
  QUEUE_SIZE: 'queue_size',
  REQUEST_LATENCY_DURATION: 'request_latency_duration',
  RESPONSE_SIZE_BYTES: 'response_size_bytes',
  // CCPE-425: SIP-driven failed-task handling (feature-use-sip-events-for-failed-tasks).
  WEBPHONE_FAILED_TASK_RECEIVED_TOTAL: 'webphone_failed_task_received_total',
  WEBPHONE_FAILED_TASK_HANDLED_TOTAL: 'webphone_failed_task_handled_total',
  // A lazy chunk failed to load; tagged by reason (stale deploy vs network).
  CHUNK_LOAD_ERROR_TOTAL: 'chunk_load_error_total',
  GQL_SUBSCRIPTION_RETRY_TOTAL: 'gql_subscription_retry_total',
} as const;

/**
 * Metric tag constants for categorizing metrics.
 * Use LOW cardinality values only!
 */
export const MetricTags = {
  OPERATION_NAME: 'operation_name',
  ERROR_TYPE: 'error_type',
  DIRECTION: 'direction',
  QUEUE_NAME: 'queue_name',
  SOURCE: 'source',
  // CCPE-425: how a failed task was classified, and whether this handler acted.
  REASON: 'reason',
  OUTCOME: 'outcome',
  ATTEMPT: 'attempt',
} as const;

/**
 * Source tag values for REQUEST_LATENCY_DURATION metric.
 */
export const LatencySource = {
  NETWORK: 'network',
  FRONTEND_FETCH: 'frontend_fetch',
  BACKEND_PROCESSING: 'backend_processing',
} as const;

/**
 * Source tag values for RESPONSE_SIZE_BYTES metric.
 */
export const ResponseSizeSource = {
  ON_WIRE: 'on_wire',
  DECODED: 'decoded',
} as const;

/**
 * Outcome tag values for REQUEST_LATENCY_DURATION metric.
 */
export const RequestOutcome = {
  SUCCESS: 'success',
  HTTP_ERROR: 'http_error',
} as const;

/**
 * Outcome tag values for GQL_SUBSCRIPTION_RETRY_TOTAL metric.
 */
export const SubscriptionRetryOutcome = {
  SCHEDULED: 'scheduled',
  EXHAUSTED: 'exhausted',
} as const;

export type SubscriptionRetryOutcome =
  (typeof SubscriptionRetryOutcome)[keyof typeof SubscriptionRetryOutcome];

/**
 * Operation names for metrics.
 */
export const OperationNames = {
  CLASSIFICATION: 'classification',
  SESSION_OFFERED: 'session-offered',
  CALL_PLACED: 'call-placed',
  CALL_CONNECTION: 'call-connection',
  AUDIO_DEVICE_INIT: 'audio-device-init',
  CALL_ACCEPT: 'call-accept',
  APP_READY: 'app-ready',
} as const;

/**
 * Error types for classification errors.
 */
export const ClassificationErrorTypes = {
  STATUS_NULL: 'status_null',
  NOT_ENDED: 'not_ended',
  NOT_READY: 'not_ready',
  UNHANDLED_CLASSIFICATION: 'unhandled_classification',
  EXCEPTION: 'exception',
} as const;

/**
 * Error types for call connection failures.
 * Only used when success=false.
 */
export const CallConnectionErrorTypes = {
  TIMEOUT: 'timeout',
  GENERAL: 'general',
  WARM_TRANSFER: 'warm_transfer',
} as const;

/**
 * Error category values for error classification.
 * LOW cardinality categories to avoid metric explosion.
 * Used for categorizing errors in metrics tags.
 */
export const ServerErrorCategory = {
  TIMEOUT: 'timeout',
  NETWORK: 'network',
  AUTH: 'auth',
  SERVER_ERROR: 'server_error',
  BAD_REQUEST: 'bad_request',
  UNKNOWN: 'unknown',
} as const;

function isIgnoredError(errorMessage: string) {
  return ERROR_IGNORE_LIST.some((ignore) => errorMessage.includes(ignore));
}

function trackError(errorInput: TrackErrorInput) {
  let error: Error;
  if (!errorInput.error) {
    error = new Error(errorInput.message);
  } else if (errorInput.error instanceof Error) {
    error = errorInput.error;
  } else {
    error = new Error(errorInput.message, { cause: errorInput.error });
  }

  // ignore errors from our ignore list
  if (isIgnoredError(error.message)) {
    return;
  }

  // send to Sentry (only in non-dev/test environments)
  if (errorInput.source !== 'sentry') {
    // Only send to Sentry in production-like environments
    // Skip in local development and test modes to avoid noise
    if (environment.MODE !== 'development' && environment.MODE !== 'test') {
      Sentry.captureException(error);
    } else {
      console.warn(
        `[DiagnosticsModule] Sentry disabled in ${environment.MODE} mode. Error would have been reported:`,
        error.message,
      );
    }
  }

  // Skipped sources:
  // - 'sentry': errors routed here by SentryErrorIntegration. Uncaught ones
  //   reach RUM on their own; direct Sentry.captureException calls on caught
  //   errors stay Sentry-only.
  // - 'unknown': raw console.error calls trapped by AppLogs. RUM's console
  //   wrapper already records them, so forwarding would report each twice.
  if (errorInput.source !== 'sentry' && errorInput.source !== 'unknown') {
    DatadogModule.addError(error, {
      errorType: errorInput.errorType,
      source: errorInput.source,
      priority: errorInput.priority,
    });
  }

  // emit event so other modules can use it
  appManager.events.emit('diagnostics.error', {
    key: 'diagnostics',
    message: error.message,
    args: [errorInput.extra, errorInput.source, errorInput.priority],
  });

  const item: TrackedError = {
    id: uuidv4(),
    ...errorInput,
    priority: errorInput.priority || 'normal',
    error,
    createdAt: Date.now(),
    type: 'error',
    read: false,
  };
  diagnosticsState.errors = [...diagnosticsState.errors, item];
}

export type ReportErrorOptions = Omit<
  TrackErrorInput,
  'errorType' | 'message' | 'error'
>;

export type ReportEventOptions = Omit<TrackEventInput, 'message'>;

export interface NetworkLatencyData {
  networkLatencyMs: number;
  frontendRequestDurationMs: number;
  backendProcessingDurationMs: number;
  operationName: string;
  timestamp: string;
  outcome: (typeof RequestOutcome)[keyof typeof RequestOutcome];
}

export interface FrontendFetchDurationData {
  durationMs: number;
  operationName: string;
  outcome: NetworkLatencyData['outcome'];
}

export interface ResponseSizeData {
  encodedBodySize: number;
  decodedBodySize: number;
  operationName: string;
  timestamp: string;
}

export class DiagnosticsModule extends AppModule {
  override events = ['diagnostics.error', 'diagnostics.network.latency'];

  private metricsBuffer: FrontendMetricEntry[] = [];
  private readonly METRICS_FLUSH_INTERVAL_MS = 60000; // 1 minute
  private readonly METRICS_MAX_BUFFER_SIZE = 50;
  private readonly METRICS_BUFFER_HARD_CAP = 1000; // Oldest entries are dropped past this while flushes fail
  private readonly METRICS_ENDPOINT = `${environment.API_URL}/v1/metric`;
  private readonly MAX_BACKOFF_MULTIPLIER = 4; // Max 4 minutes between retries
  private isFlushingMetrics = false;
  private metricsDroppedSinceLastLog = 0;
  private metricsFailureCount = 0;
  private metricsFlushesToSkip = 0;

  override preload(): void {
    // report event so we can see in UI
    this.app.events.onAny((event: string | string[], data: any) => {
      const message = Array.isArray(event) ? event.join(', ') : event;
      this.reportEvent(message, {
        source: 'app',
        extra: data,
      });
      this.app.logger.info('event', event, data);
    });

    // listen for console errors
    // these are handled, log.error(...) calls
    this.app.logger.on('error', (key, message, args) => {
      // report so it goes through the normal error flow
      this.reportBackgroundError(message, {
        source: key,
        priority: 'normal',
        extra: args,
      });
    });

    // Start metrics auto-flush
    this.startMetricsAutoFlush();
  }

  public reportUserError(message: string, options?: ReportErrorOptions): void;
  public reportUserError(error: unknown, options?: ReportErrorOptions): void;
  public reportUserError(
    error: unknown,
    message: string,
    options?: ReportErrorOptions,
  ): void;
  public reportUserError(
    error: any,
    message: any,
    options?: ReportErrorOptions,
  ): void {
    this.reportError(error, message, options, 'user');
  }

  public reportBackgroundError(
    message: string,
    options?: ReportErrorOptions,
  ): void;
  public reportBackgroundError(
    error: unknown,
    options?: ReportErrorOptions,
  ): void;
  public reportBackgroundError(
    error: unknown,
    message: string,
    options?: ReportErrorOptions,
  ): void;
  public reportBackgroundError(
    error: any,
    message: any,
    options?: ReportErrorOptions,
  ): void {
    this.reportError(error, message, options, 'background');
  }

  public reportEvent(message: string, options: ReportEventOptions) {
    const item: TrackedEvent = {
      extra: options.extra && this.cleanExtraData(options.extra),
      source: options.source,
      id: options.id ?? uuidv4(),
      message,
      createdAt: Date.now(),
      type: 'event',
    };
    diagnosticsState.events = [...diagnosticsState.events, item];
  }

  /**
   * Reports network latency data for debugging and metrics purposes.
   * Records a histogram metric and emits a diagnostics event with the latency details.
   *
   * @param data - The network latency data to report
   */
  public reportNetworkLatency(
    data: NetworkLatencyData,
    options: { emitEvent: boolean } = { emitEvent: true },
  ): void {
    this.app.logger.debug('network latency', data);

    this.recordHistogram(
      MetricNames.REQUEST_LATENCY_DURATION,
      data.networkLatencyMs,
      {
        [MetricTags.OPERATION_NAME]: data.operationName,
        [MetricTags.SOURCE]: LatencySource.NETWORK,
        [MetricTags.OUTCOME]: data.outcome,
      },
    );

    this.reportFrontendFetchDuration({
      durationMs: data.frontendRequestDurationMs,
      operationName: data.operationName,
      outcome: data.outcome,
    });

    this.recordHistogram(
      MetricNames.REQUEST_LATENCY_DURATION,
      data.backendProcessingDurationMs,
      {
        [MetricTags.OPERATION_NAME]: data.operationName,
        [MetricTags.SOURCE]: LatencySource.BACKEND_PROCESSING,
        [MetricTags.OUTCOME]: data.outcome,
      },
    );

    if (options.emitEvent) {
      this.app.events.emit('diagnostics.network.latency', {
        networkLatencyMs: data.networkLatencyMs,
        frontendRequestDurationMs: data.frontendRequestDurationMs,
        backendProcessingDurationMs: data.backendProcessingDurationMs,
        operationName: data.operationName,
        timestamp: data.timestamp,
        outcome: data.outcome,
      });
    }
  }

  /**
   * Records the frontend fetch duration sample. Called on its own for a
   * response that carries no backend duration; emits no latency event.
   *
   * @param data - The fetch duration to record
   */
  public reportFrontendFetchDuration(data: FrontendFetchDurationData): void {
    this.app.logger.debug('frontend fetch duration', data);

    this.recordHistogram(
      MetricNames.REQUEST_LATENCY_DURATION,
      data.durationMs,
      {
        [MetricTags.OPERATION_NAME]: data.operationName,
        [MetricTags.SOURCE]: LatencySource.FRONTEND_FETCH,
        [MetricTags.OUTCOME]: data.outcome,
      },
    );
  }

  /**
   * Reports response size data for debugging and metrics purposes.
   * Records a histogram metric for the on-wire (compressed) and decoded
   * (uncompressed) response sizes.
   *
   * @param data - The response size data to report
   */
  public reportResponseSize(data: ResponseSizeData): void {
    this.app.logger.debug('response size', data);

    this.recordHistogram(
      MetricNames.RESPONSE_SIZE_BYTES,
      data.encodedBodySize,
      {
        [MetricTags.OPERATION_NAME]: data.operationName,
        [MetricTags.SOURCE]: ResponseSizeSource.ON_WIRE,
      },
    );

    this.recordHistogram(
      MetricNames.RESPONSE_SIZE_BYTES,
      data.decodedBodySize,
      {
        [MetricTags.OPERATION_NAME]: data.operationName,
        [MetricTags.SOURCE]: ResponseSizeSource.DECODED,
      },
    );
  }

  public clearItems() {
    diagnosticsState.errors = [];
    diagnosticsState.events = [];
  }

  public markErrorsAsRead(priorities?: TrackedErrorPriority[]) {
    diagnosticsState.errors.forEach((error) => {
      if (!priorities || priorities.includes(error.priority)) {
        error.read = true;
      }
    });
  }

  public setDiagnosticsDrawerOpen(open: boolean) {
    diagnosticsState.drawerOpen = open;
  }

  /**
   * Sentry integration that tracks errors through DiagnosticsModule.
   * Use this when configuring Sentry.init() integrations.
   * See {@link sentryErrorIntegration} for more details.
   */
  public static sentryIntegration() {
    return sentryErrorIntegration(trackError);
  }

  private reportError(
    arg1: any,
    arg2: any,
    arg3?: ReportErrorOptions,
    errorType: TrackedErrorType = 'user',
  ): void {
    let actualMessage: string | undefined = arg2;
    let actualError: unknown = arg1;

    if (typeof arg2 === 'object') {
      // arg2 is options, arg1 is a message or error
      arg3 = arg2;
      actualMessage = typeof arg1 === 'string' ? arg1 : undefined;
      actualError = typeof arg1 !== 'string' ? arg1 : undefined;
    }

    if (!actualMessage) {
      actualMessage = extractErrorMessage(actualError);
    }

    trackError({
      message: actualMessage,
      error: actualError,
      ...arg3,
      errorType,
    });
  }

  /**
   * Sends a metric to be tracked. The metric will be buffered and sent periodically to the BFF.
   *
   * @param name - The name of the metric (will be prefixed with "contactcenter_fe_" by the BFF)
   * @param type - The type of metric ("Counter" or "Histogram")
   * @param value - The value to record
   * @param tags - Optional tags for the metric (use LOW cardinality tags only!)
   */
  public sendMetric(
    name: string,
    type: MetricType,
    value: number,
    tags?: Record<string, string>,
  ): void {
    const metric: FrontendMetricEntry = {
      name,
      type,
      value,
      tags,
      timestamp: new Date().toISOString(),
    };

    this.metricsBuffer.push(metric);
    this.dropOldestMetricsPastCap();

    // Auto-flush if buffer is full AND not in backoff
    if (
      this.metricsBuffer.length >= this.METRICS_MAX_BUFFER_SIZE &&
      this.metricsFlushesToSkip === 0
    ) {
      void this.flushMetrics();
    }
  }

  /**
   * Increments a counter metric by 1.
   *
   * @param name - The name of the counter metric
   * @param tags - Optional tags for the metric
   */
  public incrementCounter(name: string, tags?: Record<string, string>): void {
    this.sendMetric(name, 'Counter', 1, tags);
  }

  /**
   * Records a histogram metric value.
   *
   * @param name - The name of the histogram metric
   * @param value - The value to record
   * @param tags - Optional tags for the metric
   */
  public recordHistogram(
    name: string,
    value: number,
    tags?: Record<string, string>,
  ): void {
    this.sendMetric(name, 'Histogram', value, tags);
  }

  /**
   * Records a single value (Gauge) metric.
   *
   * @param name - The name of the metric
   * @param value - The value to record
   * @param tags - Optional tags for the metric
   */
  public recordValue(
    name: string,
    value: number,
    tags?: Record<string, string>,
  ): void {
    this.sendMetric(name, 'Value', value, tags);
  }

  /**
   * Starts the automatic periodic flushing of metrics.
   */
  private startMetricsAutoFlush(): void {
    setInterval(() => {
      this.logDroppedMetrics();
      void this.flushMetrics();
    }, this.METRICS_FLUSH_INTERVAL_MS);
  }

  /** Drops the oldest buffered metrics past the hard cap and counts them. */
  private dropOldestMetricsPastCap(): void {
    const excess = this.metricsBuffer.length - this.METRICS_BUFFER_HARD_CAP;
    if (excess > 0) {
      this.metricsBuffer.splice(0, excess);
      this.metricsDroppedSinceLastLog += excess;
    }
  }

  /** Logs, once per interval tick, how many buffered metrics were dropped. */
  private logDroppedMetrics(): void {
    if (this.metricsDroppedSinceLastLog > 0) {
      this.app.logger.warn(
        'Metrics buffer full, dropped oldest entries',
        this.metricsDroppedSinceLastLog,
      );
      this.metricsDroppedSinceLastLog = 0;
    }
  }

  /** Auth expiry, timeouts, rate limits and server errors are worth retrying. */
  private isRetryableMetricsStatus(status: number): boolean {
    return status === 401 || status === 408 || status === 429 || status >= 500;
  }

  /**
   * Flushes the metrics buffer to the BFF endpoint.
   * Implements incremental backoff on failures: 60s, 120s, 180s, 240s.
   */
  private async flushMetrics(): Promise<void> {
    // Prevent concurrent flushes (race condition protection)
    if (this.isFlushingMetrics) {
      return;
    }

    // Implement backoff: skip flushes during backoff period
    if (this.metricsFlushesToSkip > 0) {
      this.metricsFlushesToSkip--;
      return;
    }

    if (this.metricsBuffer.length === 0) {
      return;
    }

    this.isFlushingMetrics = true;

    const batch = [...this.metricsBuffer];
    this.metricsBuffer = [];

    try {
      const response = await fetch(this.METRICS_ENDPOINT, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(batch),
      });

      if (!response.ok && !this.isRetryableMetricsStatus(response.status)) {
        // The endpoint will reject this batch again; drop it without backing off
        this.metricsDroppedSinceLastLog += batch.length;
        this.app.logger.error(
          'Metrics rejected by the endpoint, dropped batch',
          response.status,
          batch.length,
        );
      } else if (!response.ok) {
        // Restore metrics to buffer for retry
        this.metricsBuffer.unshift(...batch);
        this.dropOldestMetricsPastCap();
        // Increment failure count and set backoff (capped at MAX)
        this.metricsFailureCount = Math.min(
          this.metricsFailureCount + 1,
          this.MAX_BACKOFF_MULTIPLIER,
        );
        // Skip next N flushes (1, 2, 3, or 4) for incremental backoff
        this.metricsFlushesToSkip = this.metricsFailureCount;
        this.app.logger.error(
          'Failed to send metrics, will retry with backoff',
          response.status,
          response.statusText,
        );
      } else {
        // Reset failure count and backoff on success
        this.metricsFailureCount = 0;
        this.metricsFlushesToSkip = 0;
      }
    } catch (error) {
      this.metricsBuffer.unshift(...batch);
      this.dropOldestMetricsPastCap();
      // Increment failure count and set backoff on exception
      this.metricsFailureCount = Math.min(
        this.metricsFailureCount + 1,
        this.MAX_BACKOFF_MULTIPLIER,
      );
      this.metricsFlushesToSkip = this.metricsFailureCount;
      // Logged and backed off; metrics never block the app
      this.app.logger.error('Metrics flush error', error);
    } finally {
      this.isFlushingMetrics = false;
    }
  }

  /**
   * Cleans the extra data by performing a deep copy using JSON serialization.
   * If the serialization fails, it returns the original data.
   *
   * @param extra - The extra data to be cleaned.
   * @returns The cleaned extra data or the original data if serialization fails.
   */
  private cleanExtraData(extra: any) {
    try {
      return JSON.parse(JSON.stringify(extra));
    } catch (_e) {
      return extra;
    }
  }
}

export default new DiagnosticsModule(
  appManager.events,
  appManager.logger('Diagnostics'),
);
